import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Folder, FolderOpen, Lock } from 'lucide-react';
import type { NodoArbol } from '@doc/shared';

interface Props {
  arbol: NodoArbol[];
  carpetaActual: string | null;
  /** Ids de los ancestros de la carpeta abierta: la rama se despliega sola. */
  ruta: string[];
  alAbrir(id: string): void;
  alSoltar(ids: string[], destinoId: string): void;
}

export function ArbolCarpetas({ arbol, carpetaActual, ruta, alAbrir, alSoltar }: Props) {
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  const [destino, setDestino] = useState<string | null>(null);

  const hijosDe = useMemo(() => {
    const mapa = new Map<string | null, NodoArbol[]>();
    for (const n of arbol) {
      const lista = mapa.get(n.padreId) ?? [];
      lista.push(n);
      mapa.set(n.padreId, lista);
    }
    return mapa;
  }, [arbol]);

  useEffect(() => {
    setExpandidos((previos) => {
      const siguiente = new Set(previos);
      for (const id of ruta) siguiente.add(id);
      return siguiente;
    });
  }, [ruta]);

  const alternar = (id: string) =>
    setExpandidos((previos) => {
      const siguiente = new Set(previos);
      if (siguiente.has(id)) siguiente.delete(id);
      else siguiente.add(id);
      return siguiente;
    });

  function pintar(nodo: NodoArbol, nivel: number) {
    const hijos = hijosDe.get(nodo.id) ?? [];
    const abierto = expandidos.has(nodo.id);
    const accesible = nodo.permisos.leer;
    const esDestino = destino === nodo.id && nodo.permisos.escribir;

    return (
      <div key={nodo.id}>
        <div
          className={`rama${carpetaActual === nodo.id ? ' activa' : ''}${accesible ? '' : ' sin-acceso'}${
            esDestino ? ' destino' : ''
          }`}
          style={{ paddingLeft: 6 + nivel * 13 }}
          onClick={() => accesible && alAbrir(nodo.id)}
          onDragOver={(e) => {
            if (!nodo.permisos.escribir) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            setDestino(nodo.id);
          }}
          onDragLeave={() => setDestino((d) => (d === nodo.id ? null : d))}
          onDrop={(e) => {
            setDestino(null);
            if (!nodo.permisos.escribir) return;
            const carga = e.dataTransfer.getData('application/x-nodos');
            if (!carga) return;
            e.preventDefault();
            alSoltar(JSON.parse(carga) as string[], nodo.id);
          }}
          title={accesible ? nodo.nombre : `${nodo.nombre} — sin acceso`}
        >
          <span
            className="rama__flecha"
            onClick={(e) => {
              e.stopPropagation();
              if (hijos.length > 0) alternar(nodo.id);
            }}
          >
            {hijos.length > 0 ? abierto ? <ChevronDown size={13} /> : <ChevronRight size={13} /> : null}
          </span>
          <span className="rama__icono">
            {abierto && hijos.length > 0 ? <FolderOpen size={15} /> : <Folder size={15} />}
          </span>
          <span className="rama__nombre">{nodo.nombre}</span>
          {!nodo.heredaPermisos && (
            <Lock size={11} className="rama__candado" aria-label="Permisos propios (no hereda)" />
          )}
        </div>
        {abierto && hijos.map((h) => pintar(h, nivel + 1))}
      </div>
    );
  }

  const raices = hijosDe.get(null) ?? [];

  return (
    <aside className="arbol" onDragLeave={() => setDestino(null)}>
      <div className="arbol__titulo">Carpetas</div>
      {raices.map((r) => pintar(r, 0))}
    </aside>
  );
}
